import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { localTimeString } from '../helpers/time';
import { navToDevicesPage } from '../../home/Widget/helpers/helpers';

const ModalTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  background-color: white;

  th, td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid ${props => props.theme.colors.greyL};
    word-wrap: break-word;
    max-width: 200px;
  }

  th {
    background-color: ${props => props.theme.colors.greyXXL};
    font-weight: 600;
    color: ${props => props.theme.colors.greyXXD};
  }

  tbody tr:hover {
    background-color: ${props => props.theme.colors.greyXXXL};
  }

  tbody tr.clickable {
    cursor: pointer;
  }

  tbody tr.clickable:hover {
    background-color: ${props => props.theme.colors.blueXXL};
  }
`;

function BigNumberTable({ tableData, tableMetadata, switchAccount, widgetName }) {
  const formatCellValue = (value, formatType) => {
    if (value == null) return '--';

    switch (formatType) {
      case 'timestamp':
        try {
          return localTimeString(value);
        } catch (error) {
          return value;
        }
      default:
        return Array.isArray(value) ? value.join(', ') : value;
    }
  };

  if (!tableData || typeof tableData !== 'object') return null;

  const selectedKeys = tableMetadata && tableMetadata.selectedKeys;
  const columnDefinitions = tableMetadata && tableMetadata.columnDefinitions;
  if (!Array.isArray(columnDefinitions)) {
    return null;
  }

  let dataToDisplay = tableData;
  if (selectedKeys && Array.isArray(selectedKeys)) {
    dataToDisplay = selectedKeys.reduce((filtered, key) => {
      if (Object.prototype.hasOwnProperty.call(tableData, key)) {
        filtered[key] = tableData[key];
      }
      return filtered;
    }, {});
  }

  const rows = [];

  Object.entries(dataToDisplay).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Handle nested objects like serial_numbers.TX-111111
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        const row = { serial_number: nestedKey };

        // Add the nested object properties to the row
        if (typeof nestedValue === 'object' && nestedValue !== null) {
          Object.assign(row, nestedValue);
        }

        rows.push(row);
      });
    }
  });

  return (
    <div>
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <ModalTable>
          <thead>
            <tr>
              {columnDefinitions.map((colDef, index) => (
                <th key={index}>{colDef.displayName}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const isClickable = row.account_id && widgetName;
              const handleRowClick = () => {
                if (isClickable) {
                  // Create filterOptions with account_id for navigation
                  const filterOptions = [
                    { name: 'account_id', op: 'eq', value: [row.account_id] }
                  ];
                  navToDevicesPage(widgetName, 0, filterOptions, switchAccount);
                }
              };

              return (
                <tr
                  key={rowIndex}
                  className={isClickable ? 'clickable' : ''}
                  onClick={handleRowClick}
                >
                  {columnDefinitions.map((colDef, colIndex) => (
                    <td key={colIndex}>
                      {formatCellValue(row[colDef.key], colDef.formatType)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </ModalTable>
      </div>
    </div>
  );
}

BigNumberTable.propTypes = {
  tableData: PropTypes.object.isRequired,
  tableMetadata: PropTypes.shape({
    modalTitle: PropTypes.string,
    selectedKeys: PropTypes.arrayOf(PropTypes.string),
    columnDefinitions: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      formatType: PropTypes.string
    })).isRequired
  }).isRequired,
  switchAccount: PropTypes.func.isRequired,
  widgetName: PropTypes.string.isRequired
};

export default withTheme(BigNumberTable);
